import { useSyncExternalStore } from 'react';

// The ballot rows save themselves in the background (ProjectRow). Each row with unsaved
// changes registers a flusher here, so the page can save everything at once before it
// leaves for the final check or finalises, and show whether anything is still pending.
type Flusher = () => Promise<void>;

const flushers = new Map<string, Flusher>();
const listeners = new Set<() => void>();
let saving = 0;

function emit() {
  for (const listener of listeners) listener();
}

export function registerPending(id: string, flush: Flusher) {
  flushers.set(id, flush);
  emit();
}

export function clearPending(id: string) {
  if (flushers.delete(id)) emit();
}

export function trackSave<T>(promise: Promise<T>): Promise<T> {
  saving += 1;
  emit();
  return promise.finally(() => {
    saving -= 1;
    emit();
  });
}

// Saves every row with unsaved changes now, instead of waiting for its debounce.
export async function flushAll(): Promise<void> {
  await Promise.all([...flushers.values()].map((flush) => flush()));
}

// Closing the tab or leaving the site saves what is pending too: the request is a
// keepalive fetch, so it outlives the page.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    void flushAll();
  });
}

export type SaveResult = { ok: boolean; errors: Record<string, string> };

// One project's grades and note, as JSON. A plain `fetch` rather than an Inertia visit
// so a save that is still in flight when the judge navigates away neither gets
// cancelled nor drags them back to the ballot when it lands.
export async function saveScores(
  url: string,
  payload: { scores: Record<string, string>; note?: string },
): Promise<SaveResult> {
  const token =
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute('content') ?? '';
  try {
    const response = await fetch(url, {
      method: 'PATCH',
      credentials: 'same-origin',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': token,
      },
      body: JSON.stringify(payload),
    });
    const body = (await response.json().catch(() => ({}))) as Partial<SaveResult>;
    return { ok: response.ok, errors: body.errors ?? {} };
  } catch {
    return { ok: false, errors: {} };
  }
}

function snapshot(): string {
  return `${flushers.size}:${saving}`;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// "saving" while a request is in flight or a change waits for its debounce.
export function useAutosaveStatus(): 'idle' | 'saving' {
  const state = useSyncExternalStore(subscribe, snapshot, snapshot);
  return state === '0:0' ? 'idle' : 'saving';
}
